import { ScopeContext } from '@sentry/types'
import { l } from '@manychat/manyui'

import { alert } from 'common/core'
import {
  BusinessError,
  isBusinessError,
  isResponseError,
  ResponseError,
} from 'shared/api/lib/errors/business'
import { reportUnhandledErrors } from 'shared/api/lib/errors/utils'
import errorTrackingService from 'utils/services/errorTrackingService'
import { MarkupOptions } from 'utils/services/errorTrackingService/interfaces/MarkupOptions'

type CatchHandler = (error: unknown, onBusinessErrors?: (errors: ResponseError) => void) => void

const errorMessage = l.makeTranslate('Something went wrong on the server. Please try again.')

/**
 * @deprecated Use `handleError` from 'shared/api/lib/errors/handlers' instead.
 */
export const handleCatch: CatchHandler = (error, onBusinessErrors) => {
  if (!isResponseError(error)) {
    errorTrackingService.trackError(error)
    alert(l.getString(errorMessage), 'danger')
    return
  }

  if (onBusinessErrors) {
    onBusinessErrors(error)
    return
  }

  reportUnhandledErrors(error.$errors, error.endpoint)
}

export interface BusinessErrorWithHandle extends BusinessError {
  handle: VoidFunction
  originalErrors: BusinessError[]
}

export interface ExtendedMarkupOptions extends MarkupOptions {
  tags?: ScopeContext['tags']
}

type BusinessErrorHandler = (error: BusinessErrorWithHandle) => void

const onExceptionByTimeout =
  (error: BusinessError, endpoint?: string, tags?: ScopeContext['tags']) => () => {
    alert(error.message, 'danger')
    errorTrackingService.trackUnhandledErrors([error], endpoint, tags)
  }

const onHandle = (timerId: number) => () => {
  window.clearTimeout(timerId)
}

export function handleError(err: unknown): void
export function handleError(err: unknown, onBusinessError: BusinessErrorHandler): void
export function handleError(err: unknown, options: ExtendedMarkupOptions): void
export function handleError(
  err: unknown,
  onBusinessError: BusinessErrorHandler,
  options: ExtendedMarkupOptions,
): void

// Implementation of the overloaded handleError function
/**
 *
 * @param error  Error object that was thrown
 * @param arg2  Callback for reacting to BusinessError that occurs in the action or options
 * @param arg3  Options for error tracking service
 * @returns void
 */
export function handleError(
  err: unknown,
  arg2?: BusinessErrorHandler | ExtendedMarkupOptions,
  arg3?: ExtendedMarkupOptions,
): void {
  let onBusinessError: BusinessErrorHandler | undefined
  let options: Partial<ExtendedMarkupOptions> = {}

  if (typeof arg2 === 'function') {
    onBusinessError = arg2
    arg3 && (options = arg3)
  } else if (typeof arg2 === 'object' && arg2 !== null) {
    options = arg2
  }

  if (!isResponseError(err) && !isBusinessError(err)) {
    errorTrackingService.logError(err, {
      ...options,
      scope: options.scope ?? 'root',
      section: options.section ?? null,
    })
    alert(l.translate('Something went wrong on the server. Please try again.'), 'danger')
    return
  }

  // We take the first error in the list because in most cases we have one error in response.
  const error = isResponseError(err) ? err.$errors[0] : err
  const errors = isResponseError(err) ? err.$errors : [error]
  const errorScope = isResponseError(err) ? err.scope : 'root'
  const errorSection = isResponseError(err) ? err.section : null

  const tags: ScopeContext['tags'] = {
    scope: options.scope ?? errorScope,
    section: options.section ?? errorSection,
    ...options.tags,
  }

  if (onBusinessError) {
    const endpoint = isResponseError(err) ? err.endpoint : undefined
    const timerId = window.setTimeout(onExceptionByTimeout(error, endpoint, tags), 100)
    const extendedError: BusinessErrorWithHandle = {
      ...error,
      handle: onHandle(timerId),
      originalErrors: errors,
    }

    onBusinessError(extendedError)
  } else if (isResponseError(err)) {
    reportUnhandledErrors(err.$errors, err.endpoint, tags)
  }
}

export const makeHandleError =
  (errorMarkupOptions: ExtendedMarkupOptions) =>
  (err: unknown, onBusinessError?: BusinessErrorHandler) => {
    onBusinessError
      ? handleError(err, onBusinessError, errorMarkupOptions)
      : handleError(err, errorMarkupOptions)
  }
